// Emulação de toque para a simulação de celular/tablet do UAT.
//
// Roda só DENTRO do iframe aberto por `UatViewportShortcut` (UAT ou
// desenvolvimento local; em produção não faz nada). Faz o mouse se comportar
// como um dedo: o app recebe eventos de toque, não de mouse.
//
//   - mouse pressionado + mover  -> touchstart/touchmove/touchend (e
//     pointer events "touch"); arrastar rola a lista (com inércia), segurar
//     parado 250 ms e mover é o arrasto do dnd-kit, como no aparelho;
//   - toque curto sem mover -> os eventos de mouse "de compatibilidade" +
//     foco + click, como o navegador do celular faz depois do touchend (se o
//     app deu preventDefault no touchstart/touchend, eles não saem);
//   - ponteiro "grosso": `(pointer: coarse)`, `(hover: none)` e
//     `ontouchstart` passam a valer, no JS (matchMedia) e no CSS;
//   - campo de texto focado -> avisa a janela de fora, que mostra o teclado
//     virtual e encolhe o iframe; as teclas dele voltam por postMessage.
//
// Este módulo precisa ser o PRIMEIRO import do `index.js`: o matchMedia e as
// folhas de estilo são ajustados antes de qualquer outro módulo usá-los.

import { isEmbeddedFrame, isTestEnvironment } from "./environment";

export const KEYBOARD_MESSAGE = "relinq-uat-keyboard"; // iframe -> janela
export const KEY_MESSAGE = "relinq-uat-key"; // janela -> iframe

const TAP_MAX_MOVE = 10; // px: acima disso o toque virou arrasto/rolagem
const TAP_MAX_MS = 700;
const FINGER_SIZE = 28;
const MEDIA_TRUE = "(min-width: 0px)";
const MEDIA_FALSE = "(width: 0px)";

const FOCUSABLE =
  'input:not([disabled]),textarea:not([disabled]),select:not([disabled]),button:not([disabled]),a[href],summary,[tabindex],[contenteditable]:not([contenteditable="false"])';
const NO_KEYBOARD_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "range",
  "color",
  "image",
  "hidden",
  "date",
  "time",
  "datetime-local",
  "month",
  "week",
]);

/* ---------------------------- ponteiro "grosso" --------------------------- */

const rewriteMediaQuery = (query) =>
  String(query)
    .replace(/\(\s*(?:any-)?pointer\s*:\s*coarse\s*\)/gi, MEDIA_TRUE)
    .replace(/\(\s*(?:any-)?pointer\s*:\s*fine\s*\)/gi, MEDIA_FALSE)
    .replace(/\(\s*(?:any-)?hover\s*:\s*none\s*\)/gi, MEDIA_TRUE)
    .replace(/\(\s*(?:any-)?hover\s*:\s*hover\s*\)/gi, MEDIA_FALSE);

const rewriteRules = (rules) => {
  Array.from(rules).forEach((rule) => {
    if (rule instanceof CSSMediaRule) {
      const next = rewriteMediaQuery(rule.media.mediaText);
      if (next !== rule.media.mediaText) rule.media.mediaText = next;
    }
    if (rule.cssRules) rewriteRules(rule.cssRules);
  });
};

const rewriteSheet = (sheet) => {
  try {
    if (sheet) rewriteRules(sheet.cssRules);
  } catch (error) {
    // folha de outra origem (fontes etc.): não dá para ler, e não precisa
  }
};

const patchPointerCapabilities = () => {
  try {
    Object.defineProperty(navigator, "maxTouchPoints", {
      get: () => 5,
      configurable: true,
    });
  } catch (error) {
    // ignore
  }
  if (!("ontouchstart" in window)) window.ontouchstart = null;

  const originalMatchMedia = window.matchMedia.bind(window);
  window.matchMedia = (query) => originalMatchMedia(rewriteMediaQuery(query));

  Array.from(document.styleSheets).forEach(rewriteSheet);
  document.addEventListener(
    "load",
    (event) => {
      if (event.target?.tagName === "LINK") rewriteSheet(event.target.sheet);
    },
    true,
  );
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.tagName === "STYLE" || node.tagName === "LINK") {
          rewriteSheet(node.sheet);
        }
      });
    });
  }).observe(document, { childList: true, subtree: true });
};

/* ------------------------------ dedo na tela ------------------------------ */

let finger = null;
const fingerPos = { x: -100, y: -100, pressed: false };

const ensureFinger = () => {
  if (finger || !document.body) return finger;
  const style = document.createElement("style");
  style.textContent = "html, html * { cursor: none !important; }";
  document.head.appendChild(style);
  finger = document.createElement("div");
  finger.setAttribute("aria-hidden", "true");
  Object.assign(finger.style, {
    position: "fixed",
    left: "0",
    top: "0",
    width: `${FINGER_SIZE}px`,
    height: `${FINGER_SIZE}px`,
    borderRadius: "50%",
    background: "rgba(148, 148, 160, 0.45)",
    border: "2px solid rgba(255, 255, 255, 0.85)",
    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.35)",
    pointerEvents: "none",
    zIndex: "2147483647",
    boxSizing: "border-box",
    transition: "opacity 120ms",
  });
  document.body.appendChild(finger);
  return finger;
};

const drawFinger = () => {
  if (!ensureFinger()) return;
  const half = FINGER_SIZE / 2;
  finger.style.transform = `translate(${fingerPos.x - half}px, ${
    fingerPos.y - half
  }px) scale(${fingerPos.pressed ? 0.78 : 1})`;
};

const moveFinger = (x, y) => {
  fingerPos.x = x;
  fingerPos.y = y;
  drawFinger();
  if (finger) finger.style.opacity = "1";
};

const setFingerPressed = (pressed) => {
  fingerPos.pressed = pressed;
  drawFinger();
};

/* ------------------------------ eventos de toque -------------------------- */

const pointDetails = (x, y) => ({
  clientX: x,
  clientY: y,
  screenX: x,
  screenY: y,
  pageX: x + window.scrollX,
  pageY: y + window.scrollY,
});

const fireTouch = (type, target, x, y) => {
  const touch = new window.Touch({
    identifier: 1,
    target,
    radiusX: 11,
    radiusY: 11,
    rotationAngle: 0,
    force: 0.5,
    ...pointDetails(x, y),
  });
  const active = type === "touchend" || type === "touchcancel" ? [] : [touch];
  const event = new window.TouchEvent(type, {
    bubbles: true,
    cancelable: true,
    composed: true,
    view: window,
    touches: active,
    targetTouches: active,
    changedTouches: [touch],
  });
  target.dispatchEvent(event);
  return event;
};

const firePointer = (type, target, x, y, pressed) => {
  const event = new window.PointerEvent(type, {
    bubbles: type !== "pointerenter" && type !== "pointerleave",
    cancelable: true,
    composed: true,
    view: window,
    pointerId: 1,
    pointerType: "touch",
    isPrimary: true,
    width: 22,
    height: 22,
    pressure: pressed ? 0.5 : 0,
    button: type === "pointermove" ? -1 : 0,
    buttons: pressed ? 1 : 0,
    ...pointDetails(x, y),
  });
  target.dispatchEvent(event);
  return event;
};

const fireMouse = (type, target, x, y, pressed) => {
  const event = new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    composed: true,
    view: window,
    detail: type === "mousemove" ? 0 : 1,
    button: 0,
    buttons: pressed ? 1 : 0,
    ...pointDetails(x, y),
  });
  target.dispatchEvent(event);
  return event;
};

/* --------------------------------- rolagem -------------------------------- */

const canScroll = (el, axis, by) => {
  if (axis === "y") {
    const max = el.scrollHeight - el.clientHeight;
    return by > 0 ? el.scrollTop < max - 1 : el.scrollTop > 0;
  }
  const max = el.scrollWidth - el.clientWidth;
  return by > 0 ? el.scrollLeft < max - 1 : el.scrollLeft > 0;
};

// Quem rola quando o dedo arrasta a partir de `target`: os ancestrais com
// rolagem, do mais interno ao mais externo, e a página por último.
const buildScrollChain = (target) => {
  const chain = { x: [], y: [], noPan: false };
  for (
    let el = target;
    el && el !== document.documentElement && el !== document.body;
    el = el.parentElement
  ) {
    const style = window.getComputedStyle(el);
    if (style.touchAction === "none" && !chain.x.length && !chain.y.length) {
      chain.noPan = true;
      return chain;
    }
    const scrolls = (value) =>
      value === "auto" || value === "scroll" || value === "overlay";
    if (scrolls(style.overflowY) && el.scrollHeight > el.clientHeight) {
      chain.y.push(el);
    }
    if (scrolls(style.overflowX) && el.scrollWidth > el.clientWidth) {
      chain.x.push(el);
    }
  }
  const root = document.scrollingElement;
  if (root) {
    chain.y.push(root);
    chain.x.push(root);
  }
  return chain;
};

// O dedo anda `fingerDelta`; o conteúdo acompanha, então a rolagem é o oposto.
const scrollChain = (list, axis, fingerDelta) => {
  if (!fingerDelta) return false;
  const by = -fingerDelta;
  const el = list.find((candidate) => canScroll(candidate, axis, by));
  if (!el) return false;
  if (axis === "y") el.scrollTop += by;
  else el.scrollLeft += by;
  return true;
};

let inertia = null;
const stopInertia = () => {
  if (inertia) cancelAnimationFrame(inertia.id);
  inertia = null;
};

const startInertia = (chain, velocityX, velocityY) => {
  let vx = velocityX;
  let vy = velocityY;
  let last = performance.now();
  const step = (now) => {
    const dt = Math.min(now - last, 32);
    last = now;
    const decay = 0.95 ** (dt / 16.67);
    vx *= decay;
    vy *= decay;
    if (Math.hypot(vx, vy) < 0.02) {
      inertia = null;
      return;
    }
    const movedY = scrollChain(chain.y, "y", vy * dt);
    const movedX = scrollChain(chain.x, "x", vx * dt);
    if (!movedX && !movedY) {
      inertia = null;
      return;
    }
    inertia.id = requestAnimationFrame(step);
  };
  inertia = { id: requestAnimationFrame(step) };
};

/* ------------------------------ mouse -> toque ---------------------------- */

let gesture = null;

const applyTapFocus = (target, x, y) => {
  const focusable = target.closest?.(FOCUSABLE);
  if (!focusable) {
    const active = document.activeElement;
    if (active && active !== document.body) active.blur();
    return;
  }
  if (focusable !== document.activeElement) focusable.focus();
  if (focusable.isContentEditable) {
    let range = null;
    if (document.caretRangeFromPoint) range = document.caretRangeFromPoint(x, y);
    else if (document.caretPositionFromPoint) {
      const position = document.caretPositionFromPoint(x, y);
      if (position) {
        range = document.createRange();
        range.setStart(position.offsetNode, position.offset);
        range.collapse(true);
      }
    }
    if (range && focusable.contains(range.startContainer)) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
};

const emitTap = (gestureTarget, x, y) => {
  const target = gestureTarget.isConnected
    ? gestureTarget
    : document.elementFromPoint(x, y);
  if (!target) return;
  fireMouse("mousemove", target, x, y, false);
  const down = fireMouse("mousedown", target, x, y, true);
  if (!down.defaultPrevented) applyTapFocus(target, x, y);
  fireMouse("mouseup", target, x, y, false);
  fireMouse("click", target, x, y, false);
};

const onScrollbar = (event) => {
  const root = document.documentElement;
  if (event.clientX >= root.clientWidth || event.clientY >= root.clientHeight) {
    return true;
  }
  const el = event.target;
  return Boolean(
    el &&
      el.clientWidth &&
      el.clientWidth < el.offsetWidth &&
      event.offsetX > el.clientWidth,
  );
};

const endGesture = (x, y, cancelled) => {
  const current = gesture;
  gesture = null;
  if (!current) return;
  setFingerPressed(false);
  if (cancelled) {
    firePointer("pointercancel", current.target, x, y, false);
    fireTouch("touchcancel", current.target, x, y);
    return;
  }
  firePointer("pointerup", current.target, x, y, false);
  const end = fireTouch("touchend", current.target, x, y);
  const tapped =
    !current.moved &&
    performance.now() - current.startedAt <= TAP_MAX_MS &&
    !current.startPrevented &&
    !end.defaultPrevented;
  if (tapped) {
    emitTap(current.target, x, y);
    return;
  }
  const fresh = performance.now() - current.lastAt < 60;
  const speed = Math.hypot(current.vx, current.vy);
  if (current.scrolling && fresh && speed > 0.15 && current.chain) {
    startInertia(current.chain, current.vx, current.vy);
  }
};

const setupMouseToTouch = () => {
  const swallow = (event) => {
    event.stopImmediatePropagation();
    event.preventDefault();
  };

  window.addEventListener(
    "mousedown",
    (event) => {
      if (!event.isTrusted || event.button !== 0 || onScrollbar(event)) return;
      swallow(event);
      window.focus();
      stopInertia();
      moveFinger(event.clientX, event.clientY);
      const target = event.target;
      gesture = {
        target,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        startedAt: performance.now(),
        lastAt: performance.now(),
        vx: 0,
        vy: 0,
        moved: false,
        scrolling: false,
        blocked: false,
        chain: null,
        startPrevented: false,
      };
      setFingerPressed(true);
      firePointer("pointerdown", target, event.clientX, event.clientY, true);
      const start = fireTouch("touchstart", target, event.clientX, event.clientY);
      gesture.startPrevented = start.defaultPrevented;
    },
    true,
  );

  window.addEventListener(
    "mousemove",
    (event) => {
      if (!event.isTrusted) return;
      moveFinger(event.clientX, event.clientY);
      if (!gesture) return; // sem dedo na tela: só o "cursor" se mexe
      swallow(event);
      if (event.buttons === 0) {
        endGesture(event.clientX, event.clientY, false);
        return;
      }
      const g = gesture;
      const now = performance.now();
      const dx = event.clientX - g.x;
      const dy = event.clientY - g.y;
      const dt = Math.max(now - g.lastAt, 1);
      g.x = event.clientX;
      g.y = event.clientY;
      g.lastAt = now;
      if (
        !g.moved &&
        Math.hypot(event.clientX - g.startX, event.clientY - g.startY) >
          TAP_MAX_MOVE
      ) {
        g.moved = true;
      }
      firePointer("pointermove", g.target, event.clientX, event.clientY, true);
      const move = fireTouch("touchmove", g.target, event.clientX, event.clientY);
      if (!g.moved) return;
      // Como no navegador: o app pode segurar a rolagem com preventDefault
      // (é o que o dnd-kit faz com o card já "pego"), mas só antes de a
      // rolagem começar; depois que começou, ela segue.
      if (!g.scrolling) {
        if (g.startPrevented || g.blocked || move.defaultPrevented) {
          g.blocked = true;
          return;
        }
        g.scrolling = true;
        g.chain = buildScrollChain(g.target);
      }
      if (g.chain.noPan) return;
      scrollChain(g.chain.x, "x", dx);
      scrollChain(g.chain.y, "y", dy);
      g.vx = dx / dt;
      g.vy = dy / dt;
    },
    true,
  );

  window.addEventListener(
    "mouseup",
    (event) => {
      if (!event.isTrusted || !gesture) return;
      swallow(event);
      endGesture(event.clientX, event.clientY, false);
    },
    true,
  );

  // O click de verdade é descartado: quem emite o click é o toque curto.
  // `detail` 0 = ativação por teclado (Enter/Espaço em botão), que passa.
  window.addEventListener(
    "click",
    (event) => {
      if (event.isTrusted && event.detail > 0) event.stopImmediatePropagation();
    },
    true,
  );
  window.addEventListener(
    "dblclick",
    (event) => {
      if (event.isTrusted) event.stopImmediatePropagation();
    },
    true,
  );
  ["pointerdown", "pointermove", "pointerup", "pointercancel"].forEach((type) => {
    window.addEventListener(
      type,
      (event) => {
        if (event.isTrusted && event.pointerType === "mouse") {
          event.stopImmediatePropagation();
        }
      },
      true,
    );
  });

  document.documentElement.addEventListener("mouseleave", (event) => {
    if (finger) finger.style.opacity = "0";
    if (gesture) endGesture(event.clientX, event.clientY, true);
  });
};

/* ---------------------------- teclado virtual ----------------------------- */

const isTextEntry = (el) => {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    return (
      !NO_KEYBOARD_INPUT_TYPES.has(el.type) && !el.readOnly && !el.disabled
    );
  }
  return Boolean(el.isContentEditable);
};

const keyboardLayoutFor = (el) => {
  const type = el instanceof HTMLInputElement ? el.type : "";
  const mode = String(el.inputMode || "").toLowerCase();
  return type === "number" ||
    type === "tel" ||
    mode === "numeric" ||
    mode === "decimal" ||
    mode === "tel"
    ? "numeric"
    : "text";
};

const scrollFocusedIntoView = () => {
  const el = document.activeElement;
  if (!isTextEntry(el)) return;
  const rect = el.getBoundingClientRect();
  if (rect.top < 0 || rect.bottom > window.innerHeight - 8) {
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }
};

const pressVirtualKey = (key) => {
  const el = document.activeElement;
  if (!isTextEntry(el)) return;
  el.focus();
  if (key === "Done") {
    el.blur();
    return;
  }
  if (key === "Backspace") {
    document.execCommand("delete");
    return;
  }
  if (key === "Enter") {
    const init = {
      key: "Enter",
      code: "Enter",
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
    };
    const down = new KeyboardEvent("keydown", init);
    el.dispatchEvent(down);
    if (!down.defaultPrevented) {
      if (el.isContentEditable) document.execCommand("insertParagraph");
      else if (el instanceof HTMLTextAreaElement) {
        document.execCommand("insertText", false, "\n");
      } else if (el.form && typeof el.form.requestSubmit === "function") {
        el.form.requestSubmit();
      }
    }
    el.dispatchEvent(new KeyboardEvent("keyup", init));
    return;
  }
  if (key.length === 1) document.execCommand("insertText", false, key);
};

const setupKeyboardBridge = () => {
  let lastSignature = "";
  let timer = null;

  const sync = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const active = document.activeElement;
      const open = isTextEntry(active);
      const layout = open ? keyboardLayoutFor(active) : "text";
      const signature = `${open}:${layout}`;
      if (signature === lastSignature) return;
      lastSignature = signature;
      window.parent.postMessage(
        { type: KEYBOARD_MESSAGE, open, layout },
        window.location.origin,
      );
      if (open) setTimeout(scrollFocusedIntoView, 320);
    }, 40);
  };
  document.addEventListener("focusin", sync, true);
  document.addEventListener("focusout", sync, true);

  window.addEventListener("message", (event) => {
    if (event.origin !== window.location.origin) return;
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!data || data.type !== KEY_MESSAGE || typeof data.key !== "string") {
      return;
    }
    pressVirtualKey(data.key);
  });
};

/* --------------------------------- entrada -------------------------------- */

// Firefox e Safari de desktop não têm `Touch`/`TouchEvent` (o Chrome tem). Sem
// eles não há como criar o evento, e o dnd-kit também confere
// `event instanceof TouchEvent`; então define versões mínimas, só para a
// simulação.
const ensureTouchConstructors = () => {
  if (!window.Touch) {
    window.Touch = class Touch {
      constructor(init = {}) {
        Object.assign(this, {
          radiusX: 0,
          radiusY: 0,
          rotationAngle: 0,
          force: 0,
          ...init,
        });
      }
    };
  }
  if (!window.TouchEvent) {
    // O React lê `touches.item(i)`, como numa TouchList de verdade.
    const asTouchList = (list = []) => {
      const copy = Array.from(list);
      Object.defineProperty(copy, "item", {
        value: (index) => copy[index] || null,
      });
      return copy;
    };
    window.TouchEvent = class TouchEvent extends UIEvent {
      constructor(type, init = {}) {
        super(type, init);
        this.touches = asTouchList(init.touches);
        this.targetTouches = asTouchList(init.targetTouches);
        this.changedTouches = asTouchList(init.changedTouches);
      }
    };
  }
};

const install = () => {
  if (!window.PointerEvent) {
    // eslint-disable-next-line no-console
    console.warn("Simulação de celular: este navegador não tem PointerEvent.");
    return;
  }
  ensureTouchConstructors();
  patchPointerCapabilities();
  setupMouseToTouch();
  setupKeyboardBridge();
};

if (isTestEnvironment() && isEmbeddedFrame()) install();
